import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import FeatureCard from 'tio-reimagined/components/feature-card';
import SectionHeader from 'tio-reimagined/components/section-header';
import SmartActionsSection from 'tio-employee/components/reimagined/smart-actions-section';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import PageHero from 'tio-reimagined/components/page-hero';
import GlassCard from 'tio-reimagined/components/glass-card';
import CtaBanner from 'tio-reimagined/components/cta-banner';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhGraduationCap,
  PhArrowRight,
  PhPiggyBank,
  PhBooks,
  PhTrophy,
  PhChartLineUp,
  PhBank,
  PhSparkle,
  PhRocket,
  PhChatCircleDots,
} from 'ember-phosphor-icons';

interface CollegePlanningIndexModel {
  dataSourceInfo: DataSourceInfo;
}

interface CollegePlanningIndexSignature {
  Args: {
    model: CollegePlanningIndexModel;
  };
}

interface PlanningTool {
  title: string;
  description: string;
  icon: typeof PhGraduationCap;
  chipLabel: string;
  chipIntent: 'primary' | 'success' | 'warning';
}

const PLANNING_TOOLS: PlanningTool[] = [
  {
    title: '529 Savings Calculator',
    description:
      'Calculate how much to save monthly to meet your college funding goal. See projections based on different contribution rates and investment returns.',
    icon: PhChartLineUp,
    chipLabel: 'Interactive',
    chipIntent: 'primary',
  },
  {
    title: 'Scholarship Finder',
    description:
      "Search thousands of scholarships matched to your child's profile, interests, and demographics. Updated annually with new opportunities.",
    icon: PhTrophy,
    chipLabel: 'Search Tool',
    chipIntent: 'success',
  },
  {
    title: 'College Cost Comparison',
    description:
      'Compare tuition, fees, and total cost of attendance across universities. Includes historical trending and expected future costs.',
    icon: PhBank,
    chipLabel: 'Research',
    chipIntent: 'warning',
  },
];

export default class CollegePlanningIndex extends Component<CollegePlanningIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get planningTools() {
    return PLANNING_TOOLS;
  }

  get smartActions(): SmartAction[] {
    return [
      {
        id: '529-setup',
        title: 'Open a 529 Plan',
        description:
          'Tax-advantaged savings for education expenses. Start with as little as $25/month.',
        icon: PhPiggyBank,
        priority: 'urgent',
        category: 'money',
        cta: 'Get Started',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'scholarships',
        title: 'Find Scholarships',
        description: 'Search for scholarships your family may qualify for. Free money for college.',
        icon: PhGraduationCap,
        priority: 'high',
        category: 'opportunity',
        cta: 'Search Now',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'mentoring',
        title: 'Talk to a Mentor',
        description:
          'Connect with college planning mentors who can guide your family through the process.',
        icon: PhChatCircleDots,
        priority: 'medium',
        category: 'career',
        cta: 'Find a Mentor',
        route: 'authenticated.reimagined.advisor',
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Hero Section }}
      <PageHero
        @gradientClass="bg-gradient-to-br from-amber-500 via-orange-600 to-red-700"
        @decoration="circles"
        @rounded="2xl"
      >
        <:default>
          <div class="flex items-center gap-3 mb-4">
            <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
              <PhGraduationCap class="h-7 w-7 text-white" />
            </div>
            <div>
              <Heading @level={{2}} class="text-white text-2xl font-bold">
                Planning for College
              </Heading>
              <Text class="text-amber-100 text-sm">
                Give your family a head start on higher education
              </Text>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <GlassCard
              @label="Avg. 4-Year Cost"
              @value="$104,108"
              @subtext="public university, in-state"
            />
            <GlassCard
              @label="529 Tax Benefit"
              @value="Tax-Free"
              @accent="emerald"
              @subtext="growth & qualified withdrawals"
            />
            <GlassCard @label="Scholarships" @value="$46B+" @subtext="awarded annually" />
          </div>
        </:default>
      </PageHero>

      {{! Smart Actions }}
      <SmartActionsSection
        @actions={{this.smartActions}}
        @title="Get Started"
        @subtitle="Take the first steps toward college readiness"
      />

      {{! Planning Tools }}
      <SectionHeader
        @icon={{PhChartLineUp}}
        @title="Planning Tools"
        @subtitle="Resources to plan and save smarter"
        @iconClass="text-orange-500"
      />
      <div class="space-y-4">
        {{#each this.planningTools as |tool|}}
          <div
            class="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div class="flex items-start gap-4">
              <div
                class="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/30 shrink-0"
              >
                <tool.icon class="h-6 w-6 text-amber-600" />
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-1">
                  <Heading @level={{4}} class="font-semibold">{{tool.title}}</Heading>
                  <Chip @intent={{tool.chipIntent}}>{{tool.chipLabel}}</Chip>
                </div>
                <Text class="text-gray-600 text-sm">
                  {{tool.description}}
                </Text>
              </div>
              <Button @intent="primary" @appearance="outlined" class="shrink-0">
                Explore
                <PhArrowRight class="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        {{/each}}
      </div>

      {{! 529 Basics }}
      <SectionHeader
        @icon={{PhPiggyBank}}
        @title="529 Plan Basics"
        @subtitle="Tax-advantaged college savings"
        @iconClass="text-green-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FeatureCard
          @icon={{PhPiggyBank}}
          @title="Tax-Free Growth"
          @description="Earnings in a 529 plan grow tax-deferred and withdrawals for qualified education expenses are tax-free. Many states also offer tax deductions for contributions."
          @color="green"
        />
        <FeatureCard
          @icon={{PhBooks}}
          @title="Flexible Use"
          @description="Use 529 funds for tuition, room & board, books, supplies, computers, and even K-12 tuition (up to $10k/year). Unused funds can be transferred to family members."
          @color="blue"
        />
        <FeatureCard
          @icon={{PhSparkle}}
          @title="NEW: 529 to Roth IRA"
          @description="Starting 2024, unused 529 funds can be rolled into a Roth IRA (subject to limits). This eliminates the risk of overfunding your child's education savings."
          @color="purple"
        />
        <FeatureCard
          @icon={{PhRocket}}
          @title="Start Small"
          @description="Most 529 plans have low minimums ($25-$50). Many allow automatic payroll deductions. Even small monthly contributions add up significantly over 18 years."
          @color="amber"
        />
      </div>

      {{! CTA }}
      <CtaBanner
        @heading="Need Guidance?"
        @description="Connect with a college planning mentor who can help you navigate savings, scholarships, and financial aid for your family."
        @theme="amber"
      >
        <:action>
          <Button @intent="primary">
            Talk to a Mentor
            <PhArrowRight class="ml-2 h-4 w-4" />
          </Button>
        </:action>
      </CtaBanner>
    </VStack>
  </template>
}
